import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { getMySessionDetail, type SessionDetail, type TranscriptEntryRead } from '@/api/client'
import { pollUntil } from '@/lib/poll'
import { ReportView } from '@/components/interview/ReportView'
import { RecordingPlayer } from '@/components/interview/RecordingPlayer'
import { StatusTracker } from '@/components/spectrumui/blocks/ai-assistants/status-tracker'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/motion/tabs'
import { AppShell } from '@/components/layout/AppShell'
import { Toaster } from '@/components/ui/toaster'
import { usePageMeta } from '@/hooks/use-page-meta';

// WAI-ARIA tabs pattern: click activates, arrow keys/Home/End move both focus
// and selection (selection follows focus), and only the active tab is in the
// tab order (roving tabindex).
const TABS = [
  { key: 'report', label: 'Report' },
  { key: 'transcript', label: 'Transcript' },
] as const

type TabKey = (typeof TABS)[number]['key']

/**
 * The candidate's report page, as a workspace: it fills the shell viewport
 * (no page-level scrolling) with a slim pinned row (back link + artifact
 * tabs) and the report's own panes scrolling inside. The shell page-head is
 * deliberately skipped — the dossier head inside the report IS the page
 * head, and its visible <h1> replaces the old sr-only one.
 */
export function CandidateSessionReport() {
  usePageMeta("Interview report — Aura")
  const navigate = useNavigate()
  const { sessionId } = useParams<{ sessionId: string }>()
  const [data, setData] = useState<SessionDetail | null>(null)
  const [tab, setTab] = useState<TabKey>('report')
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    if (!sessionId) return
    let cancelled = false
    setData(null)
    setError(null)
    getMySessionDetail(sessionId)
      .then(r => { if (!cancelled) setData(r) })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load session report:', {
          sessionId,
          status: err?.response?.status,
          message: err?.message
        })
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          navigate('/', { replace: true })
          return
        }
        if (err?.response?.status === 404) {
          setError('This report is not available yet.')
          return
        }
        setError('Failed to load the report. Please try again.')
      })
    return () => { cancelled = true }
  }, [sessionId, navigate])

  // load() resets both states before fetching — the stale view must clear on
  // a refresh even if the new fetch fails.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => load(), [load])

  // While the interview's report hasn't landed yet, keep polling — candidates
  // arriving from the arena used to sit on the "report usually lands within a
  // minute" screen having to keep clicking Refresh. The gate is "no report",
  // not "completed": the worker flips the session to completed a beat after
  // the browser leaves the room, and the arena now navigates here immediately,
  // so the first fetch can still race that write. The detail endpoint answers
  // 200 with report: null while the report is still generating, and pollUntil
  // only retries on rejection — so "no report yet" has to be the retry
  // condition, not a resolved value, or the poll would stop after one fetch.
  useEffect(() => {
    if (!data || data.report || !sessionId) return
    let cancelled = false
  // The budget follows the session status. Completed: the worker's report
  // pipeline is bounded (~90s) and this browser left the room before it
  // finished, so 120s covers the worst case. In progress: the report cannot
  // exist until the interview ends (10-minute hard cap) plus finalization,
  // so the budget must span a worst-case remainder of the interview — 15
  // minutes — or a "Track report" click mid-interview always ends in a
  // manual refresh. That long budget also slows down after the first minute
  // (3s → 10s): the early attempts still catch a session that was ending as
  // the page opened, but a flat 3s across 15 minutes is ~300 requests per
  // tab, and two or three open tabs would run into the detail endpoint's
  // 60/minute rate limit.
  const inProgress = data.status === 'in_progress'
  const timeoutMs = inProgress ? 15 * 60_000 : 120_000
  pollUntil(async () => {
    const fresh = await getMySessionDetail(sessionId!)
    if (!fresh.report) throw new Error('report-pending')
    return fresh
  }, {
    intervalMs: 3000,
    timeoutMs,
    slowAfterMs: inProgress ? 60_000 : undefined,
    slowIntervalMs: inProgress ? 10_000 : undefined,
    cancelled: () => cancelled,
  })
      .then(fresh => {
        if (!cancelled) setData(fresh)
      })
      .catch((err) => {
        // At the deadline pollUntil rethrows the last rejection: when the
        // report simply never landed that's the 'report-pending' sentinel —
        // expected, and the manual Refresh button remains below. Anything
        // else (auth, network) is logged so a silently dead poll is visible.
        if ((err as Error)?.message !== 'report-pending') {
          console.error('Report auto-poll failed:', { sessionId, error: err })
        }
      })
    return () => { cancelled = true }
  }, [data, sessionId])

  if (error) {
    return (
      <AppShell title="Interview report">
        <Toaster />
        <section className="panel">
          <div className="empty">
            <h3>Report not available</h3>
            <p>{error}</p>
            <div className="empty-cta">
              <Link to="/my-interviews" className="wbtn wbtn-secondary">← Back to my interviews</Link>
            </div>
          </div>
        </section>
      </AppShell>
    )
  }

  if (!data) {
    return (
      <AppShell title="Interview report">
        <Toaster />
        <div className="wloading" role="status" aria-live="polite">
          <span className="wspinner" aria-hidden="true"></span>
          <span>Loading report…</span>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell active="my-interviews">
      {/* The arena hands the candidate off here the moment the room drops, so
          the recording upload (and its failure toast) lands on this page — the
          fullscreen arena has no Toaster, and a lost recording must not look
          identical to a saved one. */}
      <Toaster />
      <div className="report-workspace">
        {/* beUI tabs own the tablist keyboard model (roving tabindex, arrow
            keys) that used to be hand-rolled here; the whole workspace sits
            inside the Tabs context so the panes' aria wiring resolves. */}
        <Tabs value={tab} onValueChange={v => setTab(v as TabKey)}>
          <div className="report-workspace-top">
            <Link to="/my-interviews" className="link-action">← Back to my interviews</Link>
            <TabsList aria-label="Interview artifacts">
              {TABS.map(t => (
                <TabsTrigger key={t.key} value={t.key}>{t.label}</TabsTrigger>
              ))}
            </TabsList>
          </div>
          <div className="report-workspace-body">
          <TabsContent value="report">
          {data.report ? (
            <div className="aura-panel-in">
              <ReportView
                report={data.report}
                sessionId={sessionId!}
                completedAt={data.completed_at}
                createdAt={data.created_at}
                transcript={data.transcript}
                integrityEvents={data.integrity_events}
                proctored={data.proctored}
                skills={data.plan?.extracted_skills}
                fill
                extraActions={<RecordingPlayer sessionId={sessionId!} candidateName={data.candidate_name} />}
              />
            </div>
          ) : (
            <div className="panel aura-panel-in">
              {/* Report generation runs on the worker after the room closes;
                  this page auto-polls while no report exists, with the manual
                  refresh as the backstop. The stage and copy follow the
                  session status instead of claiming the interview is done. */}
              <div className="report-pending">
                <StatusTracker
                  stages={[
                    { id: 'interview', label: 'Interview' },
                    { id: 'generating', label: 'Report' },
                    { id: 'ready', label: 'Ready' },
                  ]}
                  activeIndex={data.status === 'completed' ? 1 : 0}
                  detail={data.status === 'completed'
                    ? 'Your interview is complete — the report usually lands within a minute.'
                    : data.status === 'in_progress'
                      ? 'This interview is still in progress — its report appears here once it completes.'
                      : "This interview hasn't been started yet — its report appears here once it's done."}
                />
                <button type="button" className="wbtn wbtn-secondary wbtn-sm" onClick={load}>Refresh</button>
              </div>
            </div>
          )}
          </TabsContent>
          <TabsContent value="transcript">
          {data.transcript && data.transcript.length > 0 ? (
            <div className="panel aura-panel-in">
              <div className="panel-head">
                <h2>Transcript</h2>
                <span className="panel-meta">{data.transcript.length} lines</span>
              </div>
              <div className="wtranscript" role="log" aria-label="Interview transcript">
                {data.transcript.map((entry: TranscriptEntryRead, index: number) => (
                  <div key={index} className={`wtranscript-line ${entry.speaker === 'Interviewer' ? 'assistant' : ''}`}>
                    <span className="who">{entry.speaker} · {formatTimestamp(entry.timestamp_s)}</span>
                    <span className="text">{entry.text}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="panel aura-panel-in">
              <div className="empty">
                <h3>No transcript available</h3>
                <p>The transcript will appear after the interview concludes.</p>
              </div>
            </div>
          )}
          </TabsContent>
          </div>
        </Tabs>
      </div>
    </AppShell>
  )
}

function formatTimestamp(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  const remainder = Math.floor(seconds % 60)
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`
}
